import { useState } from 'react';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import classNames from 'classnames';

import { FilterContainer } from '@components/search_filters/units';
import TabToggle from '@javascript/instant_search/filters/tabToggle';

import type { AppliedFilters } from '@javascript/types/FilterSearch';

export type PriceSelectPayload = Partial<
  Pick<AppliedFilters, 'maxPrice' | 'minPrice' | 'paymentType'>
>;

type FormData = Omit<PriceSelectPayload, 'paymentType'>;

const ERROR_MESSAGE = 'Please enter a number';

const numberOrEmptyString = yup
  .string()
  .test('must be a number or empty', ERROR_MESSAGE, (value) => {
    if (value === '') return true; // allow empty string
    return !isNaN(Number(value)); // must be numeric if not empty
  });

const formSchema = yup.object({
  minPrice: numberOrEmptyString.test(
    'less than max',
    `${ERROR_MESSAGE} less than or equal to maximum`,
    function (minVal) {
      const { maxPrice } = this.parent;
      if (minVal === '' || maxPrice === '') return true; // skip if either empty
      return Number(minVal) <= Number(maxPrice);
    },
  ),
  maxPrice: numberOrEmptyString.test(
    'greater than min',
    `${ERROR_MESSAGE} greater than or equal to minimum`,
    function (maxVal) {
      const { minPrice } = this.parent;
      if (maxVal === '' || minPrice === '') return true; // skip if either empty
      return Number(maxVal) >= Number(minPrice);
    },
  ),
});

const PriceSelect = ({
  onFilterUpdate,
  selected: { paymentType: initialPaymentType, minPrice, maxPrice },
}: {
  onFilterUpdate: (data: PriceSelectPayload) => void;
  selected: PriceSelectPayload;
}) => {
  const [paymentType, setPaymentType] = useState<
    NonNullable<PriceSelectPayload['paymentType']>
  >(initialPaymentType === 'cash' ? 'cash' : 'monthly');

  const priceOrPayment =
    paymentType === 'monthly' ? 'monthly payment' : 'cash price';

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormData>({
    mode: 'onSubmit',
    defaultValues: { minPrice, maxPrice },
    resolver: yupResolver(formSchema),
  });

  const onSubmit = ({ minPrice, maxPrice }: FormData) => {
    onFilterUpdate({
      paymentType,
      minPrice,
      maxPrice,
    });
  };

  const handleTabToggle = (value: 'monthly' | 'cash') => {
    // change the payment type and reset min/max price values
    setPaymentType(value);
    setValue('minPrice', '');
    setValue('maxPrice', '');
  };

  return (
    <FilterContainer
      handleClick={handleSubmit(onSubmit)}
      title="Select a price"
    >
      <TabToggle
        onChange={handleTabToggle as (value: string) => void}
        option1={{ label: 'Monthly payment', value: 'monthly' }}
        option2={{ label: 'Cash price', value: 'cash' }}
        selectedOption={paymentType}
      />
      <form className="ch-mt--4 ch-mb--6">
        <div
          className={classNames('ch-form__group ch-mb--4', {
            'ch-form__group--error': !!errors.minPrice,
          })}
        >
          <label className="ch-fs--3 ch-text--left" htmlFor="min">
            Minimum {priceOrPayment}
          </label>
          {errors.minPrice && (
            <span className="ch-form__control-validation">
              {errors.minPrice?.message}
            </span>
          )}
          <input
            className="ch-form__control ch-bc--grey-6"
            id="min"
            inputMode="numeric"
            type="number"
            {...register('minPrice')}
          />
        </div>
        <div
          className={classNames('ch-form__group', {
            'ch-form__group--error': !!errors.maxPrice,
          })}
        >
          <label className="ch-fs--3 ch-mt--1 ch-text--left " htmlFor="max">
            Maximum {priceOrPayment}
          </label>
          {errors.maxPrice && (
            <span className="ch-form__control-validation">
              {errors.maxPrice?.message}
            </span>
          )}
          <input
            className="ch-form__control ch-bc--grey-6"
            id="max"
            inputMode="numeric"
            type="number"
            {...register('maxPrice')}
          />
        </div>
      </form>
    </FilterContainer>
  );
};

export default PriceSelect;
